import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAppStore } from "../store/appStore";
import { IconCheck, IconLoader } from "../components/Icons";

interface TenantDetail {
  id: string;
  name: string;
  legal_name: string | null;
  company_id: string | null;
  vat_id: string | null;
  email: string | null;
  phone: string | null;
  locale: string;
  is_active: boolean;
  plan: string;
  is_vat_payer: boolean;
  billing_address: string | null;
  billing_city: string | null;
  billing_postal_code: string | null;
  billing_country: string;
  email_verified_at: string | null;
  registered_at: string | null;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
}

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tenantId = useAppStore((s) => s.tenantId);
  const setTenantId = useAppStore((s) => s.setTenantId);
  const setTenant = useAppStore((s) => s.setTenant);

  useEffect(() => {
    if (id && id !== tenantId) {
      setTenantId(id);
    }
  }, [id, tenantId, setTenantId]);

  const { data: tenant, isLoading, error } = useQuery({
    queryKey: ["ops-tenant", id],
    queryFn: async () => {
      if (!id) throw new Error("No tenant id");
      const res = await api.get<TenantDetail>(`/ops/tenants/${id}`);
      setTenant({ id: res.data.id, name: res.data.name });
      return res.data;
    },
  });

  const [form, setForm] = useState<Partial<TenantDetail>>({});

  useEffect(() => {
    if (tenant) setForm({ ...tenant });
  }, [tenant]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No tenant id");
      await api.put(`/ops/tenants/${id}`, form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops-tenant", id] });
      qc.invalidateQueries({ queryKey: ["ops-tenants"] });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No tenant id");
      await api.post(`/ops/tenants/${id}/verify-email`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-tenant", id] }),
  });

  const releaseMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No tenant id");
      const res = await api.post<{ released: number }>(`/ops/tenants/${id}/release-emails`);
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["ops-tenant", id] });
      qc.invalidateQueries({ queryKey: ["ops-tenant-users"] });
      alert(t("tenantDetail.releaseEmailsDone", { count: data.released }));
    },
    onError: () => alert(t("tenantDetail.releaseEmailsError")),
  });

  function update<K extends keyof TenantDetail>(key: K, value: TenantDetail[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  if (isLoading) {
    return <div style={{ padding: 80, textAlign: "center" }}><IconLoader size={32} className="muted" /></div>;
  }

  if (error || !tenant) {
    return <div className="card" style={{ padding: 32, color: "var(--muted)" }}>{t("tenantDetail.loadError")}</div>;
  }

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{t("nav.tenants")} — {tenant.name}</h1>
          <p>{t("tenantDetail.title")}</p>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          {tenant.email_verified_at ? (
            <span className="badge badge-green"><IconCheck size={14} /> {t("tenantDetail.verified")}</span>
          ) : (
            <button onClick={() => verifyMutation.mutate()} className="primary" disabled={verifyMutation.isPending}>
              {t("tenantDetail.verifyEmail")}
            </button>
          )}
          <button
            onClick={() => {
              if (confirm(t("tenantDetail.releaseEmailsConfirm"))) releaseMutation.mutate();
            }}
            disabled={releaseMutation.isPending}
            style={{ borderColor: "var(--danger, #dc2626)", color: "var(--danger, #dc2626)" }}
          >
            {t("tenantDetail.releaseEmails")}
          </button>
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); updateMutation.mutate(); }}
        className="card"
        style={{ display: "grid", gap: 20 }}
      >
        <div className="grid grid-2">
          <div>
            <label>{t("tenantDetail.name")}</label>
            <input value={form.name ?? ""} onChange={(e) => update("name", e.target.value)} />
          </div>
          <div>
            <label>{t("tenantDetail.legalName")}</label>
            <input value={form.legal_name ?? ""} onChange={(e) => update("legal_name", e.target.value)} />
          </div>
        </div>
        <div className="grid grid-2">
          <div>
            <label>{t("tenantDetail.companyId")}</label>
            <input value={form.company_id ?? ""} onChange={(e) => update("company_id", e.target.value)} />
          </div>
          <div>
            <label>{t("tenantDetail.vatId")}</label>
            <input value={form.vat_id ?? ""} onChange={(e) => update("vat_id", e.target.value)} />
          </div>
        </div>
        <div className="grid grid-2">
          <div>
            <label>{t("tenantDetail.email")}</label>
            <input type="email" value={form.email ?? ""} onChange={(e) => update("email", e.target.value)} />
          </div>
          <div>
            <label>{t("tenantDetail.phone")}</label>
            <input value={form.phone ?? ""} onChange={(e) => update("phone", e.target.value)} />
          </div>
        </div>
        <div className="grid grid-2">
          <div>
            <label>{t("tenantDetail.plan")}</label>
            <input value={form.plan ?? ""} onChange={(e) => update("plan", e.target.value)} />
          </div>
          <div>
            <label>{t("tenantDetail.locale")}</label>
            <input value={form.locale ?? ""} onChange={(e) => update("locale", e.target.value)} />
          </div>
        </div>
        <div className="grid grid-2">
          <div>
            <label>{t("tenantDetail.billingAddress")}</label>
            <input value={form.billing_address ?? ""} onChange={(e) => update("billing_address", e.target.value)} />
          </div>
          <div>
            <label>{t("tenantDetail.billingCity")}</label>
            <input value={form.billing_city ?? ""} onChange={(e) => update("billing_city", e.target.value)} />
          </div>
        </div>
        <div className="grid grid-2">
          <div>
            <label>{t("tenantDetail.billingPostalCode")}</label>
            <input value={form.billing_postal_code ?? ""} onChange={(e) => update("billing_postal_code", e.target.value)} />
          </div>
          <div>
            <label>{t("tenantDetail.billingCountry")}</label>
            <input value={form.billing_country ?? ""} onChange={(e) => update("billing_country", e.target.value)} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.is_vat_payer ?? false}
              onChange={(e) => update("is_vat_payer", e.target.checked)}
            />
            {t("tenantDetail.isVatPayer")}
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.is_active ?? false}
              onChange={(e) => update("is_active", e.target.checked)}
            />
            {t("tenantDetail.isActive")}
          </label>
        </div>
        <div>
          <button type="submit" className="primary" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? <IconLoader size={16} /> : t("tenantDetail.save")}
          </button>
        </div>
      </form>
    </div>
  );
}
